// ─────────────────────────────────────────────
// OFFER ENGINE — edit OFFER_RULES to change any offer anytime
// Parameters per rule:
//   minDay / maxDay   : days since lead received (0 = same day)
//   sources           : [] = all sources, ['WH','FB'] = only those
//   days              : [] = any day, [0]=Sun,[1]=Mon,...,[6]=Sat
//   startHour/endHour : 0–23 (endHour is exclusive, e.g. 24 = midnight)
//   offerKey          : unique string used in UI to identify which offer block to show
//   couponCode        : coupon to apply
//   planPeriod        : 'YY' | 'YY2' | 'YY3'
//   whPrice / otherPrice : displayed price strings per source group
// ─────────────────────────────────────────────

export const OFFER_RULES = [
    {
        // Rule 1 — Fresh lead: show verify message, standard price only
        id: 1,
        label: 'Fresh',
        minDay: 0,
        maxDay: 0,
        sources: [],           // all sources
        days: [],              // any day
        startHour: 0,
        endHour: 24,
        offerKey: 'FRESH',
        couponCode: '',
        couponCodeForWH: '',
        planPeriod: 'YY',
        whPrice: '6,000',
        otherPrice: '6,000',
    },
    {
        // Rule 2 — D0 (same day, 6PM–6PM window), 8PM–12AM: Flash EOD offer
        id: 2,
        label: 'Flash Offer',
        minDay: 0,
        maxDay: 1,             // D0 = yesterday 6PM to today 6PM = days 0–1
        sources: [],
        days: [],
        startHour: 20,
        endHour: 24,
        offerKey: 'FLASH_EOD',
        couponCode: 'OFRFLASH',
        couponCodeForWH: 'MTFLASH',
        planPeriod: 'YY',
        whPrice: '3,900',
        otherPrice: '4,800',
    },
    {
        // Rule 3 — D16–22, Sunday 4PM–12AM: Sunday offer
        id: 3,
        label: 'Sunday Special',
        minDay: 16,
        maxDay: 22,
        sources: [],
        days: [0],             // Sunday only
        startHour: 0,
        endHour: 24,
        offerKey: 'SUNDAY',
        couponCode: 'OFRSUNSPL',
        couponCodeForWH: 'MTSUNSPL',
        planPeriod: 'YY',
        whPrice: '1,999',
        otherPrice: '4,800',
    },
    {
        // Rule 4 — D3, full day: notification for splurge
        id: 4,
        label: 'Special Offer',
        minDay: 3,
        maxDay: 3,
        sources: [],
        days: [],
        startHour: 16,
        endHour: 24,
        offerKey: 'D3',
        couponCode: 'OFRSPLLTD',
        couponCodeForWH: 'MTSPLLTD',
        planPeriod: 'YY',
        whPrice: '3,900',
        otherPrice: '4,800',
    },
    {
        // Rule 5 — Cycle days: 25, 385, 745, 1105, 1465, 1825 (every 360 days from day 25, up to 5 years)
        id: 5,
        label: 'Old Leads',
        minDay: 25,
        maxDay: 1825,
        sources: [],
        days: [],
        startHour: 0,
        endHour: 24,
        offerKey: 'OLD_PL',
        couponCode: 'OFRPLCLR',
        couponCodeForWH: 'MTPLCLR',
        planPeriod: 'YY',
        whPrice: '1,999',
        otherPrice: '4,800',
    },
    {
        // Rule L — D25+, anytime: feedback clearance offer
        id: 6,
        label: 'Feedback Clearance',
        minDay: 25,
        maxDay: 9999,
        sources: [],
        days: [],
        startHour: 0,
        endHour: 24,
        offerKey: 'FDBK_CLRNC',
        couponCode: 'OFRFDBK',
        couponCodeForWH: 'MTFDBK',
        planPeriod: 'YY',
        whPrice: '3,900',
        otherPrice: '4,800',
    },
];

// Source groups — add any new source codes here
const WH_FB_SOURCES = ['WH', 'FB', 'WHATSAPP', 'FACEBOOK', 'META'];

const isWHFBSource = (source) => {
    const s = source.toUpperCase();
    return s.startsWith('FB_') || s.endsWith('_WH');
};

const getLeadAgeInDays = (sinceDTStr) => {
    const leadDate = new Date(sinceDTStr.replace(' ', 'T'));
    const now = new Date();
    return Math.floor((now - leadDate) / (1000 * 60 * 60 * 24));
};

// Returns true if lead is within the D0 flash window:
// Lead must have been received after 6PM yesterday (or today),
// i.e. lead age in hours < 24 AND lead was received at hour >= 18
// Returns true if lead was received between yesterday 6PM → today 6PM
const isInFlashWindow = (sinceDTStr) => {
    const leadDate = new Date(sinceDTStr.replace(' ', 'T'));
    const now = new Date();
    const todayAt6PM = new Date(now);
    todayAt6PM.setHours(18, 0, 0, 0);
    const yesterdayAt6PM = new Date(todayAt6PM);
    yesterdayAt6PM.setDate(yesterdayAt6PM.getDate() - 1);
    return leadDate >= yesterdayAt6PM && leadDate <= todayAt6PM;
};

const getLeadSource = () => {
    try {
        const allDataRaw = localStorage.getItem('allData');
        if (!allDataRaw) return '';
        const allData = JSON.parse(allDataRaw);
        return (allData?.source || '').toUpperCase();
    } catch { return ''; }
};

// Returns true if lead age falls on cycle: 25, 385, 745, 1105, 1465, 1825
// Formula: starts at day 25, repeats every 360 days, max 5 years (1825)
const isOldLeadCycleDay = (sinceDTStr) => {
    const ageDays = getLeadAgeInDays(sinceDTStr);
    if (ageDays < 25 || ageDays > 1825) return false;
    return (ageDays - 25) % 360 === 0;
};

export const getActiveOffer = () => {
    try {
        // ── DEV TESTING ONLY — set localStorage key 'testOffer' to any offerKey (FLASH_EOD, SUNDAY, D3)
        // const testOffer = "D3"
        // if (testOffer) {
        //     const rule = OFFER_RULES.find(r => r.offerKey === testOffer);
        //     if (rule) return { ...rule, displayPrice: rule.otherPrice, isWHFB: true };
        // }

        const allDataRaw = localStorage.getItem('allData');
        if (!allDataRaw) return null;
        const allData = JSON.parse(allDataRaw);
        const sinceDTStr = allData?.sinceDTStr;
        if (!sinceDTStr) return null;

        const now = new Date();
        const currentHour = now.getHours();
        const currentDay = now.getDay(); // 0=Sun
        const ageDays = getLeadAgeInDays(sinceDTStr);
        const source = getLeadSource();
        const isWHFB = isWHFBSource(source);

        for (const rule of OFFER_RULES) {
            // Age check — Flash EOD uses strict 6PM window logic, OLD_PL uses cycle logic
            if (rule.offerKey === 'FLASH_EOD') {
                if (!isInFlashWindow(sinceDTStr)) continue;
            } else if (rule.offerKey === 'OLD_PL') {
                if (!isOldLeadCycleDay(sinceDTStr)) continue;
            } else {
                if (ageDays < rule.minDay || ageDays > rule.maxDay) continue;
            }
            // Day of week check
            if (rule.days.length > 0 && !rule.days.includes(currentDay)) continue;
            // Hour check
            if (currentHour < rule.startHour || currentHour >= rule.endHour) continue;
            // Source check
            if (rule.sources.length > 0 && !rule.sources.some(s => source.includes(s))) continue;

            return {
                ...rule,
                isWHFB,
                couponCode: isWHFB && rule.couponCodeForWH ? rule.couponCodeForWH : rule.couponCode,
            };
        }

        return null; // No offer matched — show base/default price
    } catch {
        return null;
    }
};

export const isFlashOfferOn = () => {
    const offer = getActiveOffer();
    return offer?.offerKey === 'FLASH_EOD';
};

export const isSundayOffer = () => {
    const offer = getActiveOffer();
    return offer?.offerKey === 'SUNDAY';
};